import orderBy from 'lodash/orderBy';
import { storeToRefs } from 'pinia';
import { useSportsOrder } from '@/composable/useSportsOrder';
import { useUrl } from '@/composable/useUrl';
import { multiProviderSportConfigs } from '@/core/lib/multiProviderSportConfig';
import type { IMarketInfo } from '@/interface/IMarketInfo';
import type { ISportInfo } from '@/interface/ISport';
import { EventDateType, MarketPage, MultipleSportsZone, SportType } from '@/interface/enum';
import { SportInfo } from '@/models/SportInfo';
import { useCustomerStore } from '@/store/customerStore';
import { useToggleStore } from '@/store/toggleStore';

export function createMarketInfoIfNotExist(list: IMarketInfo[], sportType: SportType, marketPage: MarketPage) {
    const market = list.find(x => x.marketPage === marketPage);

    if (!market) {
        list.push({
            sportType,
            marketPage,
            date: EventDateType.All,
            nonLiveEventCount: 0,
            liveEventCount: 0,
            totalEventCount: 0,
        });
    }
}

export function addLiveCountToTodayCount(list: IMarketInfo[]) {
    const liveMarket = list.find(x => x.marketPage === MarketPage.Live);
    const todayMarket = list.find(x => x.marketPage === MarketPage.Today);
    if (liveMarket && todayMarket) {
        todayMarket.liveEventCount = liveMarket.liveEventCount;
        todayMarket.totalEventCount += liveMarket.liveEventCount;
    }
}

export function processDefaultSportsOrderPipe(list: ISportInfo[]) {
    const { sportsOrder } = useSportsOrder();
    return orderBy(list, listItem => sportsOrder.value.indexOf(listItem.sportType));
}

export function processMultiProviderSportsPipe(list: ISportInfo[]): ISportInfo[] {
    const { sboCurrency, playerType } = storeToRefs(useCustomerStore());
    const popupHeight = Math.floor(window.screen.height * 0.8);

    return multiProviderSportConfigs.reduce((acc, config) => {
        if (!config.isChildrenAvailable({ sboCurrency: sboCurrency.value, playerType: playerType.value })) return acc;

        const sboSport = acc.find(item => item.sportType === config.sboSportType) ?? new SportInfo({ sportType: config.sboSportType });

        const externalSport = new SportInfo({
            sportType: config.externalSportType,
            onClick: () => {
                window.open(config.getExternalUrl(), config.popupWindowName, `width=1280,height=${popupHeight}`);
            },
        });
        const parentNodeSportInfo = new SportInfo({
            sportType: config.displaySportType,
            children: [sboSport, externalSport],
        });

        return [...acc.filter(item => item !== sboSport), parentNodeSportInfo];
    }, list);
}

export function processExcludeSportPipe(list: ISportInfo[]) {
    const { excludeSportList } = storeToRefs(useToggleStore());
    return list.filter(listItem => !excludeSportList.value.includes(listItem.sportType));
}

export function processVirtualSportsPipe(list: ISportInfo[]): ISportInfo[] {
    const { isVirtualFootballEnabled, isVirtualBasketballEnabled } = storeToRefs(useToggleStore());
    const height = Math.floor(window.screen.height * 0.8);

    const virtualSports = new Map([
        [SportType.VirtualFootball, { isEnabled: isVirtualFootballEnabled.value }],
        [SportType.VirtualBasketball, { isEnabled: isVirtualBasketballEnabled.value }],
    ]);

    const sportInfoList = [...list];
    virtualSports.forEach((virtualSport, sportType) => {
        if (!virtualSport.isEnabled) return;

        const sportInfo = new SportInfo({
            sportType,
            onClick: () => {
                const { getVirtualSportsUrl } = useUrl();
                window.open(getVirtualSportsUrl(sportType), 'VirtualSports', `width=1280,height=${height}`);
            },
        });

        sportInfoList.push(sportInfo);
    });

    return sportInfoList;
}

export const processMultipleSportsZonePipe = (multipleSportsZone: MultipleSportsZone) => (list: ISportInfo[]) => {
    if (multipleSportsZone === MultipleSportsZone.Olympic) {
        return list.filter(x => x.sportType !== SportType.Olympic);
    }

    return list;
};
